import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { getPaddleEnvironment } from "@/lib/paddle";
import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";

export type SubscriptionStatus = "trial" | "active" | "expired" | "blocked";

export interface Subscription {
  id: string;
  user_id: string;
  product: string;
  status: string;
  trial_start: string;
  trial_end: string;
  paid_until: string | null;
  activated_by_admin: boolean;
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
  slots_limit: number;
  plan_type: string;
  environment: string;
}

export function useSubscription(product: string = "audits_bpf") {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const { data: subscription, isLoading, refetch } = useQuery({
    queryKey: ["subscription", user?.id, product, getPaddleEnvironment()],
    queryFn: async () => {
      if (!user) return null;
      const environment = getPaddleEnvironment();
      const { data, error } = await supabase
        .from("subscriptions")
        .select("*")
        .eq("user_id", user.id)
        .eq("product", product)
        .maybeSingle();

      if (error) throw error;

      // If it's a paid subscription, ensure it matches the current environment
      // Trials are always allowed to show regardless of environment
      if (data && data.status !== "trial" && !data.activated_by_admin && data.environment !== environment) {
        return null;
      }

      return data as Subscription | null;
    },
    enabled: !!user,
  });

  useEffect(() => {
    if (searchParams.get("checkout") === "success") {
      refetch();
      const newParams = new URLSearchParams(searchParams);
      newParams.delete("checkout");
      setSearchParams(newParams, { replace: true });
    }
  }, [searchParams, refetch, setSearchParams]);

  const { data: slotsUsed = 0 } = useQuery({
    queryKey: ["empresas-count", user?.id],
    queryFn: async () => {
      if (!user) return 0;
      const { count, error } = await supabase
        .from("empresas")
        .select("id", { count: "exact", head: true })
        .eq("user_id", user.id);
      if (error) throw error;
      return count ?? 0;
    },
    enabled: !!user,
  });

  const computedStatus = (): SubscriptionStatus => {
    if (!subscription) return "expired";
    if (subscription.activated_by_admin) return "active";
    if (subscription.status === "active" && subscription.paid_until) {
      return new Date(subscription.paid_until) > new Date() ? "active" : "expired";
    }
    if (subscription.status === "trial") {
      return new Date(subscription.trial_end) > new Date() ? "trial" : "blocked";
    }
    return "expired";
  };

  const daysRemaining = (): number => {
    if (!subscription) return 0;
    const end = subscription.paid_until
      ? new Date(subscription.paid_until)
      : new Date(subscription.trial_end);
    const diff = Math.ceil((end.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
    return Math.max(0, diff);
  };

  const slotsLimit = subscription?.slots_limit ?? 1;
  const slotsRemaining = Math.max(0, slotsLimit - slotsUsed);
  const slotsAtLimit = slotsUsed >= slotsLimit;

  return {
    subscription,
    status: computedStatus(),
    daysRemaining: daysRemaining(),
    slotsUsed,
    slotsLimit,
    slotsRemaining,
    slotsAtLimit,
    planType: subscription?.plan_type ?? "trial",
    isLoading,
    refetch,
  };
}